/**
 * Phone-number management — iceberger-back PhoneNumberController.
 * Number provisioning itself happens server-side against the carrier
 * (Twilio) through meetings-back; this API is the system of record.
 */
import type {
  TAvailablePhoneNumber,
  TMyPhoneNumber,
  TPhoneNumber,
  TPhoneNumberSearchPayload,
  TPurchasePhoneNumberPayload,
} from "@/models/phone-number.model";
import axios from "@utils/axios-instance";

export const getPhoneNumbers = async (includeReleased = false): Promise<TPhoneNumber[]> => {
  const response = await axios.get("/phone-number", {
    params: includeReleased ? { includeReleased: true } : undefined,
  });
  return response.data ?? [];
};

export const getMyPhoneNumber = async (): Promise<TMyPhoneNumber> => {
  const response = await axios.get("/phone-number/me");
  return response.data ?? {};
};

/** Up to ten purchasable numbers for a country + area code. */
export const searchPhoneNumbers = async (
  payload: TPhoneNumberSearchPayload
): Promise<TAvailablePhoneNumber[]> => {
  const response = await axios.post("/phone-number/search", payload);
  return response.data?.numbers ?? [];
};

/** Buys the number from the carrier; 201 means it is live. */
export const purchasePhoneNumber = async (
  payload: TPurchasePhoneNumberPayload
): Promise<TPhoneNumber> => {
  const response = await axios.post("/phone-number", payload);
  return response.data;
};

/** Reassign a LINE number: a user id, or null to return it to the pool. */
export const assignPhoneNumber = async (
  id: number,
  userId: number | null
): Promise<TPhoneNumber> => {
  const response = await axios.patch(`/phone-number/${id}/assignment`, { userId });
  return response.data;
};

/** Releases the number back to the carrier. Kept as history (RELEASED). */
export const releasePhoneNumber = async (id: number): Promise<void> => {
  await axios.delete(`/phone-number/${id}`);
};
